import { ApiResponse } from 'apisauce';

import {
  DeletedMerchantDocPayload,
  MerchantTablePayload,
  MerchantUsersPayload
} from '@/Modules/MerchatsModule/shared/types/merchant-auto-payload.type';
import api from '@/shared/config/http.config';
import { ErrorInfo } from '@/shared/interfaces/error-info.interface';
import { ResponseModel } from '@/shared/interfaces/response-model.interface';
import {
  BranchDataModel,
  BranchesModel,
  CompanyContactPersonModel,
  CompanyDetailsModel,
  GetMerchantDocumentsModel,
  MerchantDocumentsDataModel,
  MerchantModel,
  MerchantUserModel,
  MerchantUsersModel
} from '@/shared/models/merchant.model';

const apis = {
  // create merchant data
  createCompanyDetails: (
    companyDetails: CompanyDetailsModel
  ): Promise<ApiResponse<{ id: string }, ErrorInfo>> => api.post('/companies/main', companyDetails),

  createCompanyLogo: (data: FormData): Promise<ApiResponse<ErrorInfo | undefined>> =>
    api.post('/companies/logo', data),

  createCompanyContactPerson: (
    contactPersonDetails: CompanyContactPersonModel
  ): Promise<ApiResponse<ResponseModel<{ id: string }>, ErrorInfo>> =>
    api.post('/companies/contact-person', contactPersonDetails),

  createBranchData: (
    branches: BranchesModel
  ): Promise<ApiResponse<ResponseModel<{ ids: string[] }>, ErrorInfo>> =>
    api.post('/branches', branches),

  // get merchant data
  getCompanyById: (id: string): Promise<ApiResponse<MerchantModel, ErrorInfo>> =>
    api.get(`/companies/${id}`),

  getCompanyBranches: (
    id: string
  ): Promise<ApiResponse<ResponseModel<BranchDataModel[]>, ErrorInfo>> =>
    api.get(`/branches/company/${id}`),

  // update merchant data
  updateCompanyDetails: (
    companyDetails: CompanyDetailsModel
  ): Promise<ApiResponse<ResponseModel<CompanyDetailsModel>, ErrorInfo>> =>
    api.put('/companies/main', companyDetails),

  updateCompanyContactPerson: (
    contactPersonDetails: CompanyContactPersonModel
  ): Promise<ApiResponse<ResponseModel<CompanyContactPersonModel>, ErrorInfo>> =>
    api.put('/companies/contact-person', contactPersonDetails),

  updateBranchData: (
    branch: BranchDataModel
  ): Promise<ApiResponse<ResponseModel<BranchDataModel>, ErrorInfo>> =>
    api.put('/branches', branch),

  deleteBranch: (id: string): Promise<ApiResponse<{ message: string }, ErrorInfo>> =>
    api.delete(`/branches/${id}`),

  updateCompanyStatus: (
    id: string,
    companyStatus: 'active' | 'disabled'
  ): Promise<ApiResponse<ResponseModel<MerchantModel>, ErrorInfo>> =>
    api.put('/companies/status', { id, companyStatus }),

  // get auto-table data
  searchCompanies: (
    searchCompanies: MerchantTablePayload
  ): Promise<ApiResponse<ResponseModel<MerchantModel[]>, ErrorInfo>> =>
    api.post('/companies/search', searchCompanies),

  // company users
  createCompanyUser: (
    user: MerchantUserModel
  ): Promise<ApiResponse<ResponseModel<{ id: string }>, ErrorInfo>> =>
    api.post('/companies/users', user),

  searchCompanyUsers: (
    searchUser: MerchantUsersPayload
  ): Promise<ApiResponse<ResponseModel<MerchantUsersModel[]>, ErrorInfo>> =>
    api.post('/companies/users/search', searchUser),

  deleteCompanyUser: (userId: string): Promise<ApiResponse<{ message: string }, ErrorInfo>> =>
    api.delete(`/companies/users/${userId}`),

  // merchant documents
  createMerchantDocuments: (
    merchantDocuments: FormData
  ): Promise<ApiResponse<{ message: string }, ErrorInfo>> =>
    api.post('/companies/upload', merchantDocuments, {
      headers: { Accept: 'multipart/form-data' }
    }),

  getMerchantDocumentsById: (
    merchantId: string
  ): Promise<ApiResponse<GetMerchantDocumentsModel[], ErrorInfo>> =>
    api.get(`/companies/documents/${merchantId}`),

  deleteMerchantDocument: (
    document: DeletedMerchantDocPayload
  ): Promise<ApiResponse<ResponseModel<MerchantDocumentsDataModel>, ErrorInfo>> =>
    api.delete('/companies/document', {}, { data: document })
};

// TODO: Enhance the way of mocking APIs
export const merchantApi = apis;
// export const merchantApi = debugConfig.useFixtures ? userApiFixtureService : apis;
